<template>
  <div data-view="scheduled-pins">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame title="Pins" has-before>
      <template v-slot:header-actions>
        <v-btn
          color="primary"
          @click="$router.push({ name: addRoute, query: $route.query })"
        >
          <v-icon>add</v-icon>
          {{ $t('add_new_pin') }}
        </v-btn>
      </template>

      <template v-slot:before>
        <NavigationTabs
          :tabs="navigationTabs"
          :tabs-items-counter="tabsItemsCounter"
        />
      </template>

      <template v-slot:content>
        <PinsFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :items="pins"
          :columns="columns"
          :pages="totalPages"
          :pagination="pagination"
          :title="tableTitle"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectPin"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'start_date'">
              <PinDate
                v-if="hasDate(scope.cell.item.start_date)"
                :date="scope.cell.item.start_date"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'end_date'">
              <PinDate
                v-if="hasDate(scope.cell.item.end_date)"
                :date="scope.cell.item.end_date"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'deal'">
              <PinThread :thread="scope.cell.item" />
            </template>

            <template v-else-if="scope.cell.column.value === 'status'">
              <StatusPanel
                table-group="pins"
                :status="scope.cell.item.status.name"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'page'">
              <PinPage :pin="scope.cell.item" />
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import PinDate from '../../components/panels/Date';
  import StatusPanel from '../../components/panels/Status';
  import PinPage from '../../components/panels/pins/Page';
  import PinThread from '../../components/panels/Thread';
  import PinsFilters from '../../components/forms/filters/Pins';
  import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import { isStr, extractNestedProp } from '@/utils';
  import columns from '../../config/pins/columns';
  import navigationTabs from '../../config/pins/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs'

  export default {
    mixins: [urlPaginationRestore],
    components: {
      NavigationTabs,
      PinsFilters,
      PinDate,
      StatusPanel,
      PinPage,
      PinThread,
    },
    data() {
      return {
        navigationTabs,
        pagination: {
          page: 1,
          rowsPerPage: import.meta.env.VUE_APP_API_MAX_ITEMS_PER_PAGE,
          totalItems: 0,
          search: '',
          sortBy: 'name',
          order: 'asc' ,
        },
        query: {},
        totalPages: 0,
        columns,
        isLoading: false,
      };
    },
    computed: {
      tabsItemsCounter() {
        if(this.$route.name === 'running-pins') {
          return [null, this.pagination.totalItems];
        }
        return [this.pagination.totalItems];
      },
      addRoute() {
        return this.$route.name === 'running-pins' ? 'running-pins-new' : 'pins-new';
      },
      pins() {
        return this.$store.getters['pins/items'];
      },
      pinsMeta() {
        return this.$store.getters['pins/itemsMeta'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      tableTitle() {
        return `${this.pagination.totalItems || 0} pins`;
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        // A query-only change is not a navigation. The filter form writes itself
        // to the URL on every apply, and without this guard both `to.name` and
        // `from.name` are 'pins', so the check below refetched a list the filter
        // had already fetched — two requests per apply.
        if (to.name === from.name) return;

        const parentRoutes = ['pins', 'running-pins'];

        // Reload when switching between pins/running-pins. A dialog route
        // returning after a save refetches via onSaved instead.
        if (parentRoutes.includes(to.name) && parentRoutes.includes(from.name)) {
          this.getPins(this.query);
        }
      },
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getPins(this.query);
      },
      getPins({ page, countries } = {}) {
        // todo check if order and sortBy needed
        const query = {
          page,
          per_page: this.pagination.rowsPerPage,
          countries,
        };

        if (this.$route.name === 'running-pins') {
          query.type = 'running'
        }

        this.isLoading = true;
        return this.$store
          .dispatch('pins/getItems', query)
          .then((res) => {
            const { total, current_page, last_page } = this.pinsMeta;
            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;
            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.runInitialFetch(() => this.getPins(nextQuery));
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getPins(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          page: 1,
          ...this.getSearchQuery(filters)
        };

        this.runInitialFetch(() => this.getPins(this.query));
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        const nextQuery = { sort: sortBy };

        this.query = nextQuery;
        this.getPins(nextQuery);
      },
      getSearchQuery({ countries }) {
        const query = {};
        query.countries = countries;

        return query;
      },
      selectPin(pin) {
        this.$store.dispatch('pins/setActiveItem', pin);
        const editRoute = this.$route.name === 'running-pins' ? 'running-pins-edit' : 'pins-edit';
        this.$router.push({ name: editRoute, params: { id: pin.id }, query: this.$route.query });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      hasDate(date) {
        return isStr(date);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='scheduled-pins'] {
  .v-datatable {
    min-width: $table-min-width;
  }

  [data-component-group='lists'] {
    &[data-component='comments'] {
      max-width: 350px;
    }
  }

  [data-component-group='panels'] {
    &[data-component='status'] {
      font-size: $text-size-sm;
      font-weight: bold;
    }
  }
}
</style>
